import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Minus, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { PublicProduct } from "@/hooks/usePublicMenu";

interface TabletProductCardProps {
  product: PublicProduct;
  quantity: number;
  onAdd: (notes?: string) => void;
  onRemove: () => void;
  onUpdateNotes?: (notes: string) => void;
}

export function TabletProductCard({
  product,
  quantity,
  onAdd,
  onRemove,
}: TabletProductCardProps) {
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState("");

  const handleAddWithNotes = () => {
    onAdd(notes || undefined);
    setNotes("");
    setShowNotes(false);
  };

  return (
    <div
      className={cn(
        "bg-slate-800/50 rounded-2xl overflow-hidden border-2 transition-all duration-300",
        quantity > 0
          ? "border-primary shadow-lg shadow-primary/20"
          : "border-slate-700 hover:border-slate-600"
      )}
    >
      {/* Product image */}
      <div className="relative aspect-[4/3] bg-slate-900">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover select-none"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-600">
            <div className="text-6xl">🍽️</div>
          </div>
        )}

        {/* Clickable overlay for reliable touch */}
        <button
          type="button"
          className="absolute inset-0 w-full h-full bg-transparent cursor-pointer z-10"
          onClick={() => onAdd()}
          aria-label={`Ver detalhes de ${product.name}`}
        />

        {/* Quantity badge */}
        {quantity > 0 && (
          <div className="absolute top-3 right-3 bg-primary text-primary-foreground w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold shadow-lg pointer-events-none z-20">
            {quantity}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <button
          type="button"
          className="w-full text-left cursor-pointer"
          onClick={() => onAdd()}
        >
          <h3 className="text-lg font-bold text-white line-clamp-1">
            {product.name}
          </h3>
          {product.description && (
            <p className="text-sm text-slate-400 line-clamp-2 mt-1">
              {product.description}
            </p>
          )}
        </button>

        {/* Price and actions */}
        <div className="flex items-center justify-between gap-3">
          <div className="text-2xl font-bold text-primary">
            R$ {product.price.toFixed(2).replace(".", ",")}
          </div>

          {quantity === 0 ? (
            <Button
              size="lg"
              className="flex-1 h-12 text-base font-semibold"
              onClick={() => onAdd()}
            >
              <Plus className="w-5 h-5 mr-1" />
              Adicionar
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="outline"
                className="h-10 w-10 border-slate-600 text-white hover:bg-slate-700"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              >
                <Minus className="w-5 h-5" />
              </Button>
              <span className="text-xl font-bold text-white w-8 text-center">
                {quantity}
              </span>
              <Button
                size="icon"
                className="h-10 w-10"
                onClick={(e) => {
                  e.stopPropagation();
                  onAdd();
                }}
              >
                <Plus className="w-5 h-5" />
              </Button>
            </div>
          )}
        </div>

        {/* Notes toggle */}
        {quantity > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-slate-400 hover:text-white"
            onClick={() => setShowNotes(!showNotes)}
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Adicionar observação
          </Button>
        )}

        {/* Notes input */}
        {showNotes && (
          <div className="space-y-2">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ex: Sem cebola, bem passado..."
              className="w-full h-20 bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white placeholder:text-slate-500 resize-none text-sm"
            />
            <Button
              size="sm"
              className="w-full"
              onClick={handleAddWithNotes}
            >
              Adicionar com observação
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
